<template>
    <div class="tw:flex tw:flex-col tw:gap-4 tw:pb-4">
        <!-- Loading state (skeleton front) -->
        <CardFront v-if="loadingAsientos" custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-8 tw:flex tw:items-center tw:justify-center tw:min-h-[300px]">
                <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
                    <div
                        class="tw:w-8 tw:h-8 tw:border-2 tw:border-gray-300 tw:border-t-blue-500 tw:rounded-full tw:animate-spin"
                    ></div>
                    <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                        {{ $t('perfil.cambio_asiento.cargando_mapa') }}
                    </p>
                </div>
            </div>
        </CardFront>

        <!-- Error -->
        <CardFront v-else-if="errorAsientos" custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-6">
                <div
                    class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm"
                >
                    {{ errorAsientos }}
                </div>
            </div>
        </CardFront>

        <!--
            Mapa general del recinto (tipo_proceso_online=3).

            Se recupera el SVG del recinto completo (`GET /f/recinto/{recinto_id}`,
            mismo endpoint que la venta de abonos online) y las butacas de todo el
            recinto. El SVG se renderiza con Leaflet y cada butaca se mapea por
            `svg_id` (mismo patrón que `SeleccionarAsientosMapaGeneral`).
        -->
        <template v-else-if="svgParsed">
            <CardFront custom-classes="tw:px-0 tw:py-0 tw:overflow-hidden">
                <LeafletComponent
                    :svg-rendered="svgParsed"
                    :plazas-seleccionadas="plazasSeleccionadas"
                    :original-color="COLOR_LIBRE"
                    :disabled="false"
                    @is-panning="onIsPanning"
                />
            </CardFront>

            <!-- Butaca seleccionada (radiogroup accesible) -->
            <CardFront
                v-if="asientoSeleccionado"
                custom-classes="tw:px-0 tw:py-0"
                role="radiogroup"
                :aria-label="$t('perfil.cambio_asiento.butaca_seleccionada')"
            >
                <div class="tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3">
                    <div class="tw:flex tw:items-center tw:gap-3">
                        <div
                            class="tw:w-9 tw:h-9 tw:rounded-full tw:bg-blue-900/20 tw:flex tw:items-center tw:justify-center"
                        >
                            <i class="material-icons tw:text-blue-900 tw:text-xl">event_seat</i>
                        </div>
                        <div>
                            <p
                                class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-blue-900 tw:mb-0.5"
                            >
                                {{ $t('perfil.cambio_asiento.butaca_seleccionada') }}
                            </p>
                            <p class="tw:text-sm tw:font-semibold tw:text-gray-800 tw:m-0">
                                {{ $t('perfil.cambio_asiento.fila') }}
                                {{ asientoSeleccionado.row || asientoSeleccionado.fila }}
                                —
                                {{ $t('perfil.cambio_asiento.butaca') }}
                                {{ asientoSeleccionado.number || asientoSeleccionado.asiento }}
                            </p>
                        </div>
                    </div>
                    <button
                        class="tw:text-sm tw:text-gray-400 hover:tw:text-red-500 tw:transition-colors tw:cursor-pointer tw:border-0 tw:bg-transparent"
                        :title="$t('perfil.cambio_asiento.quitar_butaca')"
                        @click="deselectSeat"
                    >
                        <i class="material-icons tw:text-lg">close</i>
                    </button>
                </div>

                <!-- Tipo de abono selector (solo si múltiples opciones) -->
                <template v-if="preciosDisponibles.length > 1">
                    <div class="tw:px-4 tw:pb-3">
                        <label
                            class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-gray-400 tw:mb-1 tw:block"
                        >
                            {{ $t('perfil.cambio_asiento.seleccionar_tipo_abono') }}
                        </label>
                        <select
                            class="tw:w-full tw:border tw:border-gray-200 tw:rounded-lg tw:px-3 tw:py-2 tw:text-sm tw:focus:border-blue-400 tw:focus:ring-1 tw:focus:ring-blue-400 tw:outline-none"
                            :value="tipoAbonoSeatId"
                            @change="$emit('update:tipo-abono-seat-id', Number($event.target.value))"
                        >
                            <option :value="null" disabled>
                                {{ $t('perfil.cambio_asiento.seleccionar_tipo_abono') }}
                            </option>
                            <option
                                v-for="precio in preciosDisponibles"
                                :key="precio.id"
                                :value="precio.id"
                            >
                                {{ precio.descripcion }}
                            </option>
                        </select>
                    </div>
                </template>
            </CardFront>

            <!-- Legend -->
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:px-1">
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0CBA31] tw:flex-shrink-0"></span>
                    <span
                        class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500"
                    >
                        {{ $t('perfil.cambio_asiento.disponible') }}
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#CC0000] tw:flex-shrink-0"></span>
                    <span
                        class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500"
                    >
                        {{ $t('perfil.cambio_asiento.ocupado') }}
                    </span>
                </div>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <span
                        class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-[#0F256E] tw:ring-2 tw:ring-blue-300 tw:flex-shrink-0"
                    ></span>
                    <span
                        class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500"
                    >
                        {{ $t('perfil.cambio_asiento.seleccionado') }}
                    </span>
                </div>
            </div>
        </template>

        <!-- Empty state (SVG sin butacas o sin parsear) -->
        <CardFront v-else custom-classes="tw:px-0 tw:py-0">
            <div class="tw:p-6 tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center">
                <i class="material-icons tw:text-3xl tw:text-gray-300">event_busy</i>
                <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                    {{ $t('perfil.cambio_asiento.sin_butacas_disponibles') }}
                </p>
            </div>
        </CardFront>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import LeafletComponent from '@/components/svg/components/LeafletComponent.vue';
    import { useSvgSeatMap } from '@/composables/front/perfil/useSvgSeatMap';

    // ─── Props ──────────────────────────────────────────────────────────────
    const props = defineProps({
        /** String SVG del mapa general del recinto (de `GET /f/recinto/{id}`) */
        svgString:               { type: String,  default: '' },
        /** Datos de butacas del recinto (configuracion_asientos) */
        asientosData:            { type: Object,  default: null },
        /** Estado de carga */
        loadingAsientos:         { type: Boolean, default: false },
        /** Mensaje de error al cargar */
        errorAsientos:           { type: [String, Boolean], default: null },
        /** Asiento actualmente seleccionado (null si ninguno) */
        asientoSeleccionado:     { type: Object,  default: null },
        /** Precios disponibles para la butaca seleccionada (filtrados por renovación) */
        preciosDisponibles:      { type: Array,   default: () => [] },
        /** Tipo de abono seleccionado (precio.id de la butaca) */
        tipoAbonoSeatId:         { type: [Number, String], default: null },
    });

    const emit = defineEmits(['toggle-asiento', 'quitar-asiento', 'update:tipo-abono-seat-id']);

    /**
     * Array con la butaca seleccionada (o vacío) para LeafletComponent.
     *
     * Contrato de la prop `plazasSeleccionadas`: array de objetos con `id`
     * (identidad para el diff del watch de Leaflet), `svg_id` (localiza el
     * círculo con `[id^="butaca-${svg_id}-"]`) y `precios` (si `length > 0`
     * restaura `originalColor`, si no `gray`). Pasar `[]` constante rompe la
     * cadena: el watch de Leaflet sobre `clonedPlazasSeleccionadas` nunca
     * dispara y la butaca queda marcada al quitar desde la X. Este computed
     * replica el patrón legacy (`SeleccionarAsientosMapaGeneral`): la X emite
     * `quitar-asiento`, el padre null-ifica `asientoSeleccionado` y este
     * computed vuelve a `[]`, disparando el repintado de Leaflet.
     */
    const plazasSeleccionadas = computed(() =>
        props.asientoSeleccionado ? [props.asientoSeleccionado] : []
    );

    // ─── SVG 3D seat map ───────────────────────────────────────────────────
    // IMPORTANTE: se pasan las props REALES del componente (reactivas). El
    // composable observa `props.svgString` + `props.asientosData` y genera
    // `svgParsed`; si se pasa un objeto proxy plano la reactividad se rompe
    // y `svgParsed` nunca se rellena (bug: "no se muestra nada").
    const {
        svgParsed,
        onIsPanning,
        deselectSeat,
        COLOR_LIBRE,
    } = useSvgSeatMap(props, emit);
</script>
